import type {
  ExpertGoalCheck,
  ExpertGoalCheckKind,
  ExpertGoalContract,
  ExpertGoalEvidenceSource,
  ExpertGoalExternalFact,
  ExpertGoalOnBlock,
  ExpertGoalState,
  OrchestratorIntentEnvelope,
  OrchestratorIntentKind,
} from "@dante/contracts";
import type { CompiledContextLedger, ContextRecord } from "./context-compiler.js";
import { renderCompiledLedgerBlock } from "./context-compiler.js";
import { escapePromptData } from "./prompt-data.js";

/** Thread messages the organizer digest quotes, newest last. */
export const ORGANIZER_RECENT_MESSAGE_LIMIT = 20;
/** Longest single thread message the digest quotes before marking it truncated. */
export const ORGANIZER_MESSAGE_MAX_CHARS = 1_200;
/** Hard cap on the conversation digest, so a long thread cannot crowd out the rules. */
export const ORGANIZER_DIGEST_MAX_CHARS = 12_000;
/** Compacted records the digest renders; pinned obligations are kept ahead of the newest facts. */
export const ORGANIZER_LEDGER_RECORD_LIMIT = 40;
export const ORGANIZER_NARRATIVE_MAX_CHARS = 2_000;
/** Expert goals and blockers the digest lists. */
export const ORGANIZER_GOAL_LIMIT = 20;
/** Independent requests one user turn may decompose into. */
export const ORGANIZER_CLAUSE_LIMIT = 6;

export interface OrganizerThreadMessage {
  id?: string;
  /** Thread position, rendered so the organizer can cite the message it answers. */
  seq?: number;
  role: string;
  /** The message's plain text. Untrusted: it is escaped before it reaches the prompt. */
  text: string;
  createdAt?: string;
}

export interface BuildOrganizerPromptContextOptions {
  spaceId: string;
  threadId: string;
  /** `Thread.historyCompiledLedger`; unknown shapes are ignored rather than trusted. */
  historyCompiledLedger?: unknown;
  recentMessages: readonly OrganizerThreadMessage[];
  /** The expert goals Orchestrator 2 is running, blocked, or about to start. */
  activeGoals?: readonly ExpertGoalState[];
  recentMessageLimit?: number;
  digestMaxChars?: number;
}

export interface OrganizerPromptContext {
  /** Static Organizer instructions: role, hard rules, and the JSON output contract. */
  systemPrompt: string;
  /** Dynamic conversation state: compacted ledger, recent messages, goals, and blockers. */
  conversationDigest: string;
  /** `systemPrompt` followed by `conversationDigest`, the text a provider sends. */
  prompt: string;
}

/**
 * A candidate /goal contract the organizer proposes for one request. It is deliberately looser
 * than `ExpertGoalContract`: `doneWhen` statements are seeds for the Wrangler's contract compiler,
 * which binds each one to a command, a path, or a transcript fact before an expert runs.
 */
export interface OrganizerGoalDraft {
  outcome: string;
  doneWhen: string[];
  mustNot: string[];
  /** Specialty tags the Wrangler uses to staff or reuse an expert bot. */
  specialty: string[];
  context?: string;
  budgetTurns?: number;
  budgetMinutes?: number;
  onBlock?: ExpertGoalOnBlock;
}

/** One intent envelope plus the draft contract fields the organizer proposed for it. */
export interface OrganizerIntentEnvelope extends OrchestratorIntentEnvelope {
  draft?: OrganizerGoalDraft;
}

export interface OrganizerTurnDecision {
  /** What the organizer says to the person this turn. */
  reply: string;
  /** True when the turn ends with a question instead of work. */
  clarification: boolean;
  /** Envelopes handed to Orchestrator 2, in the order the person asked for them. */
  intents: OrganizerIntentEnvelope[];
  /** Questions the person still has to answer before the turn can act. */
  questions: string[];
  /** Diagnostics about output that was dropped or repaired; never shown to the person. */
  warnings: string[];
}

export interface ParseOrganizerDecisionOptions {
  /** Raw droid-turbo output: a JSON object, a fenced JSON block, or plain prose. */
  output: string;
  spaceId: string;
  threadId: string;
  userMessageId?: string;
  /** Injected clock so a parsed decision is deterministic in tests. */
  now?: string;
  /** Injected id source; defaults to `organizer-intent-<n>:<kind>`. */
  idFactory?: (index: number, kind: OrchestratorIntentKind) => string;
}

export interface ReconcileAmbiguousPromptOptions {
  /** The person's message this turn, used to repair or decompose what the organizer returned. */
  prompt: string;
  decision: OrganizerTurnDecision;
  spaceId: string;
  threadId: string;
  userMessageId?: string;
  now?: string;
  idFactory?: (index: number, kind: OrchestratorIntentKind) => string;
}

/**
 * The Organizer's fixed instructions. It owns the conversation, never the work: every execution
 * request leaves as an intent envelope for the Wrangler, so no sandbox or domain tool is ever run
 * from this role.
 */
export const ORGANIZER_SYSTEM_PROMPT = [
  "You are the Organizer for this Space: the one assistant the person talks to in the unified main thread.",
  "You do not do the work yourself. Every piece of execution belongs to an expert goal run by the Wrangler.",
  "",
  "## What you decide on each turn",
  "- Answer directly when the digest already holds the answer or the person is only talking.",
  "- Ask one short question when the request is too vague to hand to an expert, or when two readings would produce different work.",
  "- Emit one intent envelope per independent request when the person asks for work. Split a multi-part prompt into separate goals instead of merging it into one.",
  "",
  "## Hard rules",
  "- Never execute sandbox, shell, browser, domain, or connector tools yourself, and never report work as finished. All execution work is an intent envelope for the Wrangler.",
  "- Never invent goal ids, expert names, check ids, or facts. Use only the ids and states in the conversation digest.",
  "- Keep `reply` short: it is the person-facing message for this turn, not a plan or a transcript.",
  "- A spawn envelope carries `expertDomain`, `request`, and a draft with `outcome`, `doneWhen`, `mustNot`, and `specialty`. The draft is a proposal for the Wrangler to compile and confirm; it is never proof that the work is done.",
  "- One request per envelope: `doneWhen` and `mustNot` describe that request, not the whole turn.",
  "",
  "## Output",
  "Reply with one JSON object and nothing else:",
  '{"reply": "...", "clarify": false, "intents": [{"kind": "spawn_goal", "expertDomain": "backend", "request": "...", "draft": {"outcome": "...", "doneWhen": ["..."], "mustNot": ["..."], "specialty": ["backend"]}}]}',
  "`kind` is one of direct_reply, clarify, spawn_goal, amend_goal, cancel_goal, resolve_blocker, status_query.",
  "amend_goal also carries `goalId` and a replacement `contract`; cancel_goal carries `goalId`; resolve_blocker carries `goalId` and `resolution`; status_query carries no fields.",
  "Set `clarify` to true whenever the turn ends with a question to the person instead of work.",
].join("\n");

const INTENT_KINDS: ReadonlySet<string> = new Set<OrchestratorIntentKind>([
  "direct_reply",
  "clarify",
  "spawn_goal",
  "amend_goal",
  "cancel_goal",
  "resolve_blocker",
  "status_query",
]);

/** Kinds that cross to the Wrangler and change or start expert work. */
const WORK_KINDS: ReadonlySet<OrchestratorIntentKind> = new Set<OrchestratorIntentKind>([
  "spawn_goal",
  "amend_goal",
  "cancel_goal",
  "resolve_blocker",
]);

const LIVE_GOAL_STATUSES: ReadonlySet<string> = new Set([
  "pending",
  "active",
  "verifying",
  "nudge_queued",
  "blocked",
]);

const CONTEXT_RECORD_KINDS: ReadonlySet<string> = new Set([
  "CTX_FACT",
  "CTX_DECISION",
  "CTX_OBLIGATION",
  "CTX_ENVIRONMENT",
  "CTX_TOOL_IO",
]);

const CHECK_KINDS: ReadonlySet<string> = new Set<ExpertGoalCheckKind>([
  "command_exit_0",
  "file_state",
  "grep_match",
  "test_pass",
  "external_fact",
]);

const EVIDENCE_SOURCES: ReadonlySet<string> = new Set<ExpertGoalEvidenceSource>([
  "command_output",
  "file_read",
  "test_output",
  "tool_output",
]);

const ON_BLOCK: ReadonlySet<string> = new Set<ExpertGoalOnBlock>([
  "ask_user",
  "escalate_to_organizer",
  "stop_and_report",
]);

/**
 * Build the Organizer's system prompt and conversation digest.
 *
 * The digest is what grounds the turn: compacted history (obligations first, superseded records
 * dropped), the recent unified thread, and the expert goals Orchestrator 2 already owns with the
 * blockers that need a decision from the person. Every piece of thread and goal text is escaped,
 * because a message, a goal outcome, or a blocker question is untrusted input.
 */
export function buildOrganizerPromptContext(
  options: BuildOrganizerPromptContextOptions,
): OrganizerPromptContext {
  const limit = clampInt(options.recentMessageLimit, 1, 200, ORGANIZER_RECENT_MESSAGE_LIMIT);
  const maxChars = clampInt(options.digestMaxChars, 400, 60_000, ORGANIZER_DIGEST_MAX_CHARS);
  const ledger = readCompiledLedger(options.historyCompiledLedger);
  const ledgerBlock = ledger ? renderCompiledLedgerBlock(boundCompiledLedger(ledger)).trim() : "";
  const goals = (options.activeGoals ?? []).filter(isLiveGoal).slice(0, ORGANIZER_GOAL_LIMIT);

  const header = `## Conversation State\nSpace: ${options.spaceId}\nThread: ${options.threadId}`;
  const durable = `## Durable Context\n${ledgerBlock || "(nothing compacted yet)"}`;
  const goalSection = `## Active Expert Goals\n${renderGoals(goals)}`;
  const blockerSection = `## Unresolved Blockers\n${renderBlockers(goals)}`;
  const fixedChars =
    header.length + durable.length + goalSection.length + blockerSection.length + 64;

  let messages = [...options.recentMessages].slice(-limit);
  let recent = renderRecentMessages(messages, Math.max(0, maxChars - fixedChars));
  let digest = [header, durable, `## Recent Thread\n${recent}`, goalSection, blockerSection].join(
    "\n\n",
  );

  // Shed the oldest messages in chunks until the digest fits. Goals and blockers are what the
  // organizer must act on, so they are never the part that gets cut.
  while (digest.length > maxChars && messages.length > 0) {
    messages = messages.slice(Math.max(1, Math.ceil(messages.length / 4)));
    recent = renderRecentMessages(messages, Math.max(0, maxChars - fixedChars));
    digest = [header, durable, `## Recent Thread\n${recent}`, goalSection, blockerSection].join(
      "\n\n",
    );
  }
  if (digest.length > maxChars) {
    digest = `${digest.slice(0, Math.max(0, maxChars - 20)).trimEnd()}\n[digest truncated]`;
  }

  return {
    systemPrompt: ORGANIZER_SYSTEM_PROMPT,
    conversationDigest: digest,
    prompt: `${ORGANIZER_SYSTEM_PROMPT}\n\n${digest}`,
  };
}

/**
 * Parse one droid-turbo organizer turn into a user-facing reply plus intent envelopes.
 *
 * The parser is tolerant about the wrapper (prose, code fences, a bare array) and strict about the
 * contents: unknown kinds and malformed contracts are dropped with a warning instead of being
 * guessed at, and a decision that is plain prose is kept as the reply. It never repairs a missing
 * goal id or request; `reconcileAmbiguousPrompt` does that, because repair needs the user's words.
 */
export function parseOrganizerDecision(
  options: ParseOrganizerDecisionOptions,
): OrganizerTurnDecision {
  const warnings: string[] = [];
  const parsed = extractJsonObject(options.output);
  if (!parsed) {
    const reply = collapseWhitespace(options.output);
    if (reply) warnings.push("Organizer output was not JSON; the text was kept as the reply.");
    return { reply, clarification: false, intents: [], questions: [], warnings };
  }

  const reply = readText(parsed.reply) ?? "";
  const clarification = parsed.clarify === true || parsed.clarification === true;
  const rawIntents = Array.isArray(parsed.intents) ? parsed.intents : [];
  const intents: OrganizerIntentEnvelope[] = [];
  for (const raw of rawIntents) {
    const intent = readIntent(raw, intents.length, options, warnings);
    if (intent) intents.push(intent);
  }

  return {
    reply,
    clarification: clarification || (intents.length === 0 && reply === ""),
    intents,
    questions: [],
    warnings,
  };
}

/**
 * Reconcile the organizer's decision with the words the person actually typed.
 *
 * The model can return a direct reply for a prompt that asks for several pieces of work, or an
 * envelope whose required field is missing. This pass is deterministic and grounded in the prompt:
 *
 * - an envelope that still lacks what its kind needs is dropped and turned into a question;
 * - a spawn envelope missing its domain, request, draft, or specialty is repaired from its own
 *   draft and the prompt's explicit acceptance criteria and constraints;
 * - when the decision carries no goal-changing intent, the prompt is split on sentence boundaries,
 *   list markers, "then", and a second imperative after "and", and each concrete request becomes
 *   its own `spawn_goal` envelope with a candidate outcome, Done-when, must-not, and specialty tags;
 * - a vague request ("fix it", "maybe something with tests") becomes one clarifying question
 *   instead of a goal, because guessing would spend an expert run on the wrong work.
 *
 * Nothing here executes a tool: every action the person asked for leaves as an envelope for
 * Orchestrator 2.
 */
export function reconcileAmbiguousPrompt(
  options: ReconcileAmbiguousPromptOptions,
): OrganizerTurnDecision {
  const idFactory = options.idFactory ?? defaultIntentId;
  const now = options.now ?? new Date().toISOString();
  const warnings = [...options.decision.warnings];
  const questions: string[] = [];
  const intents: OrganizerIntentEnvelope[] = [];
  const seen = new Set<string>();

  for (const intent of options.decision.intents) {
    const repaired = repairIntent(intent, { prompt: options.prompt, questions, warnings });
    if (!repaired) continue;
    const key = `${repaired.kind}:${repaired.goalId ?? ""}:${repaired.request ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    intents.push(repaired);
  }

  const reply = options.decision.reply.trim();
  if (options.decision.clarification) {
    return {
      reply: reply || questions[0] || "",
      clarification: true,
      intents,
      questions,
      warnings,
    };
  }

  if (intents.some((intent) => WORK_KINDS.has(intent.kind))) {
    return { reply, clarification: false, intents, questions, warnings };
  }

  // An intent was dropped because the organizer's own output was unusable. Ask that question
  // instead of re-reading the prompt into a fresh goal, which would act on a guess.
  if (questions.length > 0) {
    return { reply: reply || questions[0]!, clarification: true, intents, questions, warnings };
  }

  const clauses = actionableClauses(options.prompt);
  if (clauses.length === 0) {
    return { reply, clarification: false, intents, questions, warnings };
  }

  const concrete = clauses.filter((clause) => !isVagueClause(clause));
  if (concrete.length === 0) {
    const question = `What should I change for "${bounded(sentenceCase(clauses[0]!), 200)}"?`;
    questions.push(question);
    return { reply: reply || question, clarification: true, intents, questions, warnings };
  }

  const criteria = extractCriteria(options.prompt);
  const mustNot = extractMustNot(options.prompt);
  const selected = concrete.slice(0, ORGANIZER_CLAUSE_LIMIT);
  if (concrete.length > selected.length) {
    warnings.push(
      `Kept the first ${selected.length} of ${concrete.length} requests; ask the person to confirm the rest.`,
    );
  }

  for (const clause of selected) {
    const outcome = bounded(sentenceCase(clause), 400);
    const specialty = specialtyTags(clause);
    const kind: OrchestratorIntentKind = "spawn_goal";
    intents.push({
      id: idFactory(intents.length, kind),
      spaceId: options.spaceId,
      threadId: options.threadId,
      kind,
      fromRole: "organizer",
      toRole: "wrangler",
      ...(options.userMessageId ? { userMessageId: options.userMessageId } : {}),
      expertDomain: specialty[0] ?? "general",
      request: clause,
      createdAt: now,
      draft: {
        outcome,
        doneWhen: criteria.length > 0 ? criteria : [`"${outcome}" is implemented`],
        mustNot,
        specialty,
      },
    });
  }

  return { reply, clarification: false, intents, questions, warnings };
}

interface RepairContext {
  prompt: string;
  questions: string[];
  warnings: string[];
}

/** Drop what cannot be acted on, repair what the prompt can settle, and keep the rest as-is. */
function repairIntent(
  intent: OrganizerIntentEnvelope,
  context: RepairContext,
): OrganizerIntentEnvelope | null {
  switch (intent.kind) {
    case "spawn_goal": {
      const request = readText(intent.request) ?? readText(intent.draft?.outcome);
      const expertDomain = readText(intent.expertDomain) ?? readText(intent.draft?.specialty[0]);
      if (!request || !expertDomain) {
        context.warnings.push(
          "Dropped a spawn_goal intent that named no request or expert domain.",
        );
        context.questions.push(
          request
            ? `Which kind of expert should handle "${bounded(sentenceCase(request), 120)}"?`
            : "What exactly should the expert do?",
        );
        return null;
      }
      const draft = intent.draft;
      let doneWhen = draft?.doneWhen ?? [];
      if (doneWhen.length === 0) doneWhen = extractCriteria(context.prompt);
      if (doneWhen.length === 0)
        doneWhen = [`"${bounded(sentenceCase(request), 400)}" is implemented`];
      const repaired: OrganizerIntentEnvelope = {
        ...intent,
        expertDomain,
        request,
        draft: {
          outcome: readText(draft?.outcome) ?? bounded(sentenceCase(request), 400),
          doneWhen,
          mustNot: draft?.mustNot.length ? draft.mustNot : extractMustNot(context.prompt),
          specialty: draft?.specialty.length ? draft.specialty : specialtyTags(request),
          ...(draft?.context ? { context: draft.context } : {}),
          ...(draft?.budgetTurns ? { budgetTurns: draft.budgetTurns } : {}),
          ...(draft?.budgetMinutes ? { budgetMinutes: draft.budgetMinutes } : {}),
          ...(draft?.onBlock ? { onBlock: draft.onBlock } : {}),
        },
      };
      return repaired;
    }
    case "amend_goal": {
      if (!intent.goalId || (!intent.contract && !intent.draft)) {
        context.warnings.push(
          "Dropped an amend_goal intent without a goal id or replacement contract.",
        );
        context.questions.push(
          intent.goalId
            ? `What should the new contract for goal ${intent.goalId} be?`
            : "Which goal should I change, and what should it do instead?",
        );
        return null;
      }
      return intent;
    }
    case "cancel_goal": {
      if (!intent.goalId) {
        context.warnings.push("Dropped a cancel_goal intent without a goal id.");
        context.questions.push("Which goal should I cancel?");
        return null;
      }
      return intent;
    }
    case "resolve_blocker": {
      if (!intent.goalId || !readText(intent.resolution)) {
        context.warnings.push("Dropped a resolve_blocker intent without a goal id or answer.");
        context.questions.push("Which blocked goal does that answer unblock?");
        return null;
      }
      return intent;
    }
    default:
      return intent;
  }
}

function defaultIntentId(index: number, kind: OrchestratorIntentKind): string {
  return `organizer-intent-${index + 1}:${kind}`;
}

function readIntent(
  raw: unknown,
  index: number,
  options: ParseOrganizerDecisionOptions,
  warnings: string[],
): OrganizerIntentEnvelope | null {
  if (!isRecord(raw)) {
    warnings.push("Dropped an intent that was not an object.");
    return null;
  }
  const kind =
    typeof raw.kind === "string" && INTENT_KINDS.has(raw.kind)
      ? (raw.kind as OrchestratorIntentKind)
      : null;
  if (!kind) {
    warnings.push(`Dropped an intent with unknown kind ${JSON.stringify(raw.kind)}.`);
    return null;
  }

  const specialty = readTextArray(raw.specialty ?? raw.specialties ?? raw.tags);
  const draft = readDraft(raw.draft, specialty);
  const contract = readGoalContract(raw.contract);
  if (raw.contract !== undefined && !contract) {
    warnings.push(`Ignored a malformed ${kind} contract.`);
  }

  const intent: OrganizerIntentEnvelope = {
    id: (options.idFactory ?? defaultIntentId)(index, kind),
    spaceId: options.spaceId,
    threadId: options.threadId,
    kind,
    fromRole: "organizer",
    toRole: "wrangler",
    createdAt: options.now ?? new Date().toISOString(),
  };
  if (options.userMessageId) intent.userMessageId = options.userMessageId;

  const goalId = readText(raw.goalId ?? raw.goal);
  if (goalId) intent.goalId = goalId;
  const expertDomain = readText(raw.expertDomain ?? raw.domain) ?? draft?.specialty[0];
  if (expertDomain) intent.expertDomain = expertDomain;
  const request = readText(raw.request ?? raw.prompt) ?? draft?.outcome;
  if (request) intent.request = request;
  const resolution = readText(raw.resolution ?? raw.answer);
  if (resolution) intent.resolution = resolution;
  if (contract) intent.contract = contract;
  if (draft) intent.draft = draft;
  return intent;
}

function readDraft(value: unknown, fallbackSpecialty: string[]): OrganizerGoalDraft | undefined {
  if (!isRecord(value)) return undefined;
  const outcome = readText(value.outcome);
  if (!outcome) return undefined;
  const specialty = readTextArray(value.specialty ?? value.specialties ?? value.tags);
  const draft: OrganizerGoalDraft = {
    outcome: bounded(outcome, 400),
    doneWhen: readTextArray(value.doneWhen ?? value.acceptance ?? value.acceptanceCriteria).slice(
      0,
      6,
    ),
    mustNot: readTextArray(value.mustNot ?? value.guardrails ?? value.constraints).slice(0, 10),
    specialty: specialty.length > 0 ? specialty : fallbackSpecialty,
  };
  const context = readText(value.context);
  if (context) draft.context = bounded(context, 8_000);
  const budgetTurns = readInt(value.budgetTurns, 1, 200);
  if (budgetTurns !== undefined) draft.budgetTurns = budgetTurns;
  const budgetMinutes = readInt(value.budgetMinutes, 1, 1_440);
  if (budgetMinutes !== undefined) draft.budgetMinutes = budgetMinutes;
  const onBlock = readOnBlock(value.onBlock);
  if (onBlock) draft.onBlock = onBlock;
  return draft;
}

/** Accept a contract only when it already satisfies every rule the Wrangler's schema enforces. */
function readGoalContract(value: unknown): ExpertGoalContract | null {
  if (!isRecord(value)) return null;
  const outcome = readText(value.outcome);
  if (!outcome || /[\r\n]/.test(outcome) || outcome.length > 400) return null;
  const rawChecks = Array.isArray(value.doneWhen) ? value.doneWhen : null;
  if (!rawChecks || rawChecks.length < 2 || rawChecks.length > 6) return null;

  const doneWhen: ExpertGoalCheck[] = [];
  const seen = new Set<string>();
  for (const [position, raw] of rawChecks.entries()) {
    const check = readGoalCheck(raw, position);
    if (!check || seen.has(check.id)) return null;
    seen.add(check.id);
    doneWhen.push(check);
  }

  const mustNot = readTextArray(value.mustNot);
  if (mustNot.length > 10 || mustNot.some((entry) => entry.length > 300)) return null;
  const budgetTurns = readInt(value.budgetTurns, 1, 200);
  const budgetMinutes = readInt(value.budgetMinutes, 1, 1_440);
  const onBlock = readOnBlock(value.onBlock);
  const context = typeof value.context === "string" ? value.context.trim() : null;
  if (
    budgetTurns === undefined ||
    budgetMinutes === undefined ||
    !onBlock ||
    context === null ||
    context.length > 8_000
  ) {
    return null;
  }
  return { outcome, doneWhen, mustNot, budgetTurns, budgetMinutes, onBlock, context };
}

function readGoalCheck(value: unknown, position: number): ExpertGoalCheck | null {
  if (!isRecord(value)) return null;
  const id = readText(value.id);
  const statement = readText(value.statement);
  const kind =
    typeof value.kind === "string" && CHECK_KINDS.has(value.kind)
      ? (value.kind as ExpertGoalCheckKind)
      : null;
  if (!id || !statement || statement.length > 400 || !kind) return null;
  if (value.index !== position + 1) return null;
  // The organizer drafts contracts; verification evidence only ever comes from an expert run.
  if (value.verified === true) return null;

  const check: ExpertGoalCheck = { id, index: position + 1, statement, kind, verified: false };
  const command = readText(value.command);
  const path = readText(value.path);
  const pattern = readText(value.pattern);
  if (command) check.command = command;
  if (path) check.path = path;
  if (pattern) check.pattern = pattern;
  if ((kind === "command_exit_0" || kind === "test_pass") && !command) return null;
  if (kind === "file_state" && !path) return null;
  if (kind === "grep_match" && (!path || !pattern)) return null;
  if (kind === "external_fact") {
    const externalFact = readExternalFact(value.externalFact);
    if (!externalFact) return null;
    check.externalFact = externalFact;
  }
  return check;
}

function readExternalFact(value: unknown): ExpertGoalExternalFact | null {
  if (!isRecord(value)) return null;
  const tool = readText(value.tool);
  const source =
    typeof value.source === "string" && EVIDENCE_SOURCES.has(value.source)
      ? (value.source as ExpertGoalEvidenceSource)
      : null;
  const mustMatch =
    typeof value.mustMatch === "string" && value.mustMatch.length > 0 ? value.mustMatch : null;
  if (!tool || !source || !mustMatch) return null;
  const exitCode = readInt(value.exitCode, -1_000, 1_000);
  return { tool, source, mustMatch, ...(exitCode === undefined ? {} : { exitCode }) };
}

function readOnBlock(value: unknown): ExpertGoalOnBlock | undefined {
  return typeof value === "string" && ON_BLOCK.has(value)
    ? (value as ExpertGoalOnBlock)
    : undefined;
}

/**
 * Verbs that open an actionable request. A clause that does not start with one (after filler is
 * stripped) is conversation, a question, or a constraint, so it never becomes a goal by itself.
 */
const ACTION_VERB_SOURCE =
  "add|audit|build|bump|change|check|clean|configure|confirm|connect|create|debug|delete|deploy|document|ensure|expose|extract|fix|hook|implement|improve|install|investigate|make|measure|merge|migrate|move|optimi[sz]e|port|profile|publish|refactor|release|remove|rename|replace|reproduce|research|restore|revert|review|run|set up|simplify|split|support|test|upgrade|verify|wire|write";

const ACTION_VERB = new RegExp(`^(?:${ACTION_VERB_SOURCE})\\b`, "i");

/**
 * Clause boundaries: sentence punctuation, line breaks, "then", and a second imperative after
 * "and". A period inside a file name ("index.ts") is not a boundary because it is not followed by
 * whitespace.
 */
const CLAUSE_SPLIT = new RegExp(
  `[.!?;]+(?:\\s+|$)|\\n+|,\\s*(?:and\\s+)?then\\s+|\\band\\s+then\\s+|\\s+and\\s+(?=(?:also\\s+)?(?:${ACTION_VERB_SOURCE})\\b)`,
  "gi",
);

const LIST_MARKER = /^(?:[-*•]|\d+[.)])\s+/;
const CLAUSE_FILLER =
  /^(?:please|also|and|then|can you|could you|would you|will you|i want you to|i need you to|help me|let'?s)\b[\s,]*/i;

/** A request that names no object and can only be guessed at. */
const VAGUE_CLAUSE =
  /^(?:do|fix|handle|sort|deal with|look at|improve|update|change|clean up|check)\s+(?:it|this|that|these|those|them|things?|stuff|everything)\b/i;

const HEDGE =
  /\b(?:maybe|perhaps|or something|something like that|etc\.?|whatever|somehow|not sure)\b/i;

/** Clauses that state how the work is accepted, so they become candidate Done-when statements. */
const CRITERION_CLAUSE =
  /^(?:make sure|ensure|verify|confirm|check that|prove|so that|tests? (?:pass|passes|are green)|the build (?:passes|is green)|it works)\b/i;

/** Clauses that state a constraint, so they become candidate must-not guardrails. */
const MUST_NOT_CLAUSE = /^(?:do not|don't|dont|never|avoid|must not|without|no need to)\b/i;

const SPECIALTY_RULES: ReadonlyArray<{ tag: string; pattern: RegExp }> = [
  { tag: "typescript", pattern: /\b(?:typescript|ts|tsx|tsc|type(?:s|check|safety))\b/i },
  {
    tag: "frontend",
    pattern: /\b(?:react|jsx|tsx|component|hook|ui|css|tailwind|frontend|web)\b/i,
  },
  { tag: "mobile", pattern: /\b(?:ios|android|expo|react native|mobile|swift|app store)\b/i },
  { tag: "backend", pattern: /\b(?:api|server|endpoint|rpc|http|worker|queue|backend)\b/i },
  { tag: "data", pattern: /\b(?:database|sql|postgres|prisma|migration|schema|index|backfill)\b/i },
  { tag: "testing", pattern: /\b(?:test|tests|testing|vitest|playwright|e2e|coverage|spec)\b/i },
  {
    tag: "documentation",
    pattern: /\b(?:docs?|documentation|readme|changelog|comment|guide|explain)\b/i,
  },
  {
    tag: "performance",
    pattern: /\b(?:performance|perf|latency|slow|optimi[sz]e|memory|cpu|throughput)\b/i,
  },
  {
    tag: "security",
    pattern: /\b(?:security|auth|authn|authz|secret|token|permission|vulnerab\w*|cve)\b/i,
  },
  {
    tag: "devops",
    pattern: /\b(?:ci|cd|deploy|release|pipeline|docker|infra|kubernetes|terraform)\b/i,
  },
];

function promptClauses(prompt: string): string[] {
  const clauses: string[] = [];
  for (const raw of prompt.replace(/\r\n?/g, "\n").split(CLAUSE_SPLIT)) {
    const clause = stripClauseFiller(raw);
    if (clause) clauses.push(clause);
  }
  return clauses;
}

function stripClauseFiller(clause: string): string {
  let text = collapseWhitespace(clause).replace(LIST_MARKER, "");
  for (let pass = 0; pass < 4; pass += 1) {
    const next = collapseWhitespace(text.replace(CLAUSE_FILLER, ""));
    if (next === text) break;
    text = next;
  }
  return text.replace(/[.,;:!?]+$/, "").trim();
}

function actionableClauses(prompt: string): string[] {
  return promptClauses(prompt).filter(isActionableClause);
}

function isActionableClause(clause: string): boolean {
  if (clause.split(/\s+/).filter(Boolean).length < 2) return false;
  // Acceptance criteria and constraints qualify the work; they are not requests for more work.
  if (CRITERION_CLAUSE.test(clause) || MUST_NOT_CLAUSE.test(clause)) return false;
  return ACTION_VERB.test(clause) || /\/(?:goal|goal-draft)\b/i.test(clause);
}

function isVagueClause(clause: string): boolean {
  const words = clause.split(/\s+/).filter(Boolean);
  if (words.length < 2) return true;
  if (VAGUE_CLAUSE.test(clause)) return true;
  return HEDGE.test(clause) && words.length < 5;
}

function extractCriteria(prompt: string): string[] {
  return promptClauses(prompt)
    .filter((clause) => CRITERION_CLAUSE.test(clause))
    .slice(0, 4)
    .map((clause) => bounded(sentenceCase(clause), 400));
}

function extractMustNot(prompt: string): string[] {
  return promptClauses(prompt)
    .filter((clause) => MUST_NOT_CLAUSE.test(clause))
    .slice(0, 6)
    .map((clause) => bounded(sentenceCase(clause), 300));
}

function specialtyTags(text: string): string[] {
  const tags = SPECIALTY_RULES.filter((rule) => rule.pattern.test(text))
    .map((rule) => rule.tag)
    .slice(0, 4);
  return tags.length > 0 ? tags : ["general"];
}

function sentenceCase(text: string): string {
  const trimmed = collapseWhitespace(text).replace(/[.,;:!?]+$/, "");
  if (!trimmed) return "";
  return `${trimmed[0]!.toUpperCase()}${trimmed.slice(1)}`;
}

function bounded(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 12)).trimEnd()} [truncated]`;
}

function renderRecentMessages(
  messages: readonly OrganizerThreadMessage[],
  budgetChars: number,
): string {
  if (messages.length === 0) return "(no recent messages)";
  const lines: string[] = [];
  let used = 0;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const line = renderMessageLine(messages[index]!);
    if (lines.length > 0 && used + line.length + 1 > budgetChars) break;
    lines.unshift(line);
    used += line.length + 1;
  }
  return lines.join("\n");
}

function renderMessageLine(message: OrganizerThreadMessage): string {
  const speaker =
    message.role === "user" ? "Person" : message.role === "bot" ? "Organizer" : message.role;
  const seq = message.seq === undefined ? "" : `seq ${message.seq} `;
  const text = bounded(
    escapePromptData(collapseWhitespace(message.text)),
    ORGANIZER_MESSAGE_MAX_CHARS,
  );
  return `- [${seq}${speaker}] ${text}`;
}

function renderGoals(goals: readonly ExpertGoalState[]): string {
  if (goals.length === 0) return "(no active expert goals)";
  return goals.map(renderGoal).join("\n");
}

function renderGoal(goal: ExpertGoalState): string {
  const verified = goal.contract.doneWhen.filter((check) => check.verified).length;
  const total = goal.contract.doneWhen.length;
  const lines = [
    `- goal ${goal.id} [${goal.status}] domain=${goal.expertDomain} expert=${escapePromptData(goal.expertName)} checks=${verified}/${total} turns=${goal.turnsUsed}/${goal.contract.budgetTurns}: ${escapePromptData(collapseWhitespace(goal.contract.outcome))}`,
  ];
  if (goal.blocker && !goal.blocker.resolvedAt) {
    lines.push(`  blocked: ${renderBlocker(goal.blocker)}`);
    for (const check of goal.contract.doneWhen.filter((entry) => !entry.verified).slice(0, 3)) {
      lines.push(`  pending: ${escapePromptData(collapseWhitespace(check.statement))}`);
    }
  }
  return lines.join("\n");
}

function renderBlockers(goals: readonly ExpertGoalState[]): string {
  const blocked = goals.filter((goal) => goal.blocker && !goal.blocker.resolvedAt);
  if (blocked.length === 0) return "(none)";
  return blocked.map((goal) => `- goal ${goal.id}: ${renderBlocker(goal.blocker!)}`).join("\n");
}

function renderBlocker(blocker: NonNullable<ExpertGoalState["blocker"]>): string {
  const reason = escapePromptData(collapseWhitespace(blocker.reason));
  if (!blocker.question) return reason;
  return `${reason} | ask: ${escapePromptData(collapseWhitespace(blocker.question))}`;
}

function isLiveGoal(goal: ExpertGoalState): boolean {
  return LIVE_GOAL_STATUSES.has(goal.status);
}

/**
 * Bound the ledger the digest renders: pinned obligations survive, the newest facts follow, and the
 * narrative is clipped, so a long thread cannot grow the organizer's prompt without limit.
 */
function boundCompiledLedger(ledger: CompiledContextLedger): CompiledContextLedger {
  const records = ledger.records.filter((record) => record.freshness !== "superseded");
  const pinned = records
    .filter((record) => record.mustSurviveCompaction)
    .slice(-ORGANIZER_LEDGER_RECORD_LIMIT);
  const rest = records
    .filter((record) => !record.mustSurviveCompaction)
    .slice(-ORGANIZER_LEDGER_RECORD_LIMIT);
  return {
    ...ledger,
    records: [...pinned, ...rest],
    narrativeSummary: bounded(ledger.narrativeSummary.trim(), ORGANIZER_NARRATIVE_MAX_CHARS),
  };
}

function readCompiledLedger(value: unknown): CompiledContextLedger | null {
  if (!isRecord(value)) return null;
  if (value.version !== 1) return null;
  if (typeof value.compactedUpToSeq !== "number" || typeof value.generation !== "number") {
    return null;
  }
  if (typeof value.narrativeSummary !== "string" || !Array.isArray(value.records)) return null;
  if (!value.records.every(isContextRecordShape)) return null;
  return {
    version: 1,
    compactedUpToSeq: value.compactedUpToSeq,
    generation: value.generation,
    records: value.records,
    narrativeSummary: value.narrativeSummary,
  };
}

function isContextRecordShape(value: unknown): value is ContextRecord {
  if (!isRecord(value) || !isRecord(value.spanRef)) return false;
  const span = value.spanRef;
  return (
    typeof value.id === "string" &&
    typeof value.kind === "string" &&
    CONTEXT_RECORD_KINDS.has(value.kind) &&
    typeof value.subjectKey === "string" &&
    typeof value.statement === "string" &&
    typeof value.revision === "number" &&
    typeof value.freshness === "string" &&
    typeof value.mustSurviveCompaction === "boolean" &&
    typeof value.createdAt === "string" &&
    typeof span.threadId === "string" &&
    typeof span.fromSeq === "number" &&
    typeof span.toSeq === "number" &&
    typeof span.sha256 === "string"
  );
}

function extractJsonObject(output: string): Record<string, unknown> | null {
  const trimmed = output.trim();
  if (!trimmed) return null;
  const direct = tryParseJson(trimmed);
  if (direct) return direct;
  const fenced = /```(?:json)?\s*([\s\S]*?)```/i.exec(trimmed);
  if (fenced) {
    const parsed = tryParseJson(fenced[1]!.trim());
    if (parsed) return parsed;
  }
  const start = trimmed.indexOf("{");
  const end = trimmed.lastIndexOf("}");
  if (start >= 0 && end > start) return tryParseJson(trimmed.slice(start, end + 1));
  return null;
}

function tryParseJson(text: string): Record<string, unknown> | null {
  try {
    const value: unknown = JSON.parse(text);
    if (Array.isArray(value)) return { intents: value };
    return isRecord(value) ? value : null;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readText(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function readTextArray(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : typeof value === "string" ? [value] : [];
  const entries: string[] = [];
  for (const item of raw) {
    const text = readText(item);
    if (text && !entries.includes(text)) entries.push(text);
  }
  return entries;
}

function readInt(value: unknown, min: number, max: number): number | undefined {
  return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max
    ? value
    : undefined;
}

function clampInt(value: number | undefined, min: number, max: number, fallback: number): number {
  if (value === undefined || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.floor(value)));
}

function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}
